// @ai-generated - Generated by Gemini with small modifications after

import {
  SearchTreeInternalNode,
  SearchTreeLeafNode,
  SearchTreeNode,
  TemplatePlaceholderWithFilterContext,
} from '@/types';
import { getPlaceholderFiltersInLeafNode } from '@/util/searchTree/placeholderFilters';
import {
  FilterNameAndFieldMappedValue,
  TemplatePlaceholderFilterNameAndFieldToFilterValueTypeMap,
} from './types';

export type FlatSearchTreeInternalNode = {
  id: string;
  op: SearchTreeInternalNode['op'];
  childrenIds: string[];
  parentId?: string;
};

export type FlatSearchTreeLeafNode = SearchTreeLeafNode & {
  id: string;
  parentId?: string;
};

export type FlatTreeNode = FlatSearchTreeInternalNode | FlatSearchTreeLeafNode;

export function isFlatSearchTreeLeafNode(node: FlatTreeNode): node is FlatSearchTreeLeafNode {
  return node.op === 'filter' && 'filter' in node && 'name' in node.filter;
}

export type FlatSearchTree = {
  rootId: string;
  nodes: Record<string, FlatTreeNode>;
};

/**
 *
 * Flattens a SearchTreeNode into a normalized structure with unique IDs.
 *
 * @param root - The root node of the search tree.
 * @returns A flattened representation of the tree.
 */
export const flattenSearchTree = (root: SearchTreeNode): FlatSearchTree => {
  let idCounter = '-1';
  const nodes: Record<string, FlatTreeNode> = {};

  function traverse(node: SearchTreeNode, parentId?: string): string {
    idCounter = `${parseInt(idCounter) + 1}`;
    const currentId = idCounter;

    if (node.op === 'filter') {
      const leafNode = node as SearchTreeLeafNode;
      nodes[currentId] = {
        id: currentId,
        op: 'filter',
        filter: leafNode.filter,
        filterContext: leafNode.filterContext,
        parentId,
      };
    } else {
      const internalNode = node as SearchTreeInternalNode;
      const childrenIds = internalNode.nodes.map((child) => traverse(child, currentId));
      nodes[currentId] = {
        id: currentId,
        op: internalNode.op,
        childrenIds,
        parentId,
      };
    }
    return currentId;
  }

  const rootId = traverse(root);
  return { rootId, nodes };
};

/**
 * Reconstructs a nested SearchTreeNode from a flattened tree structure.
 *
 * @param flatTree - The flattened tree data.
 * @returns The nested root node of the search tree.
 */
export const unflattenSearchTree = (flatTree: FlatSearchTree): SearchTreeNode => {
  const { rootId, nodes } = flatTree;

  // Explicit check for the rootId
  if (!(rootId in nodes)) {
    throw new Error(`Root node with id ${rootId} not found`);
  }

  function buildNode(nodeId: string): SearchTreeNode {
    const flatNode = nodes[nodeId];

    // Check if any referenced node (including children) exists
    if (!flatNode) {
      throw new Error(`Child node with id ${nodeId} not found`);
    }

    if (flatNode.op === 'filter') {
      const { filter, filterContext } = flatNode;
      const leafNode: SearchTreeLeafNode = {
        op: 'filter',
        filter,
        filterContext,
      };
      return leafNode;
    } else {
      const { op, childrenIds } = flatNode;
      const internalNode: SearchTreeInternalNode = {
        op,
        nodes: childrenIds.map((childId) => buildNode(childId)),
      };
      return internalNode;
    }
  }

  return buildNode(rootId);
};

export type TemplatePlaceholderWithFilterContextAndNodeId = TemplatePlaceholderWithFilterContext & {
  nodeId: string;
};

/**
 * Each resulting placeholder has the nodeId of the node it references in
 * flatSearchTemplateTree and the full filter field path in dot notation
 * (filterNameAndField) as well as the placeholder value itself
 */
export function findPlaceholdersInFlatSearchTree(
  flatSearchTree: FlatSearchTree
): TemplatePlaceholderWithFilterContextAndNodeId[] {
  const nodesToReturn: TemplatePlaceholderWithFilterContextAndNodeId[] = [];

  Object.values(flatSearchTree.nodes).forEach((node) => {
    if (isFlatSearchTreeLeafNode(node)) {
      const newNodes = getPlaceholderFiltersInLeafNode(node).map((x) => ({
        ...x,
        nodeId: node.id,
      }));
      nodesToReturn.push(...newNodes);
    }
  });

  return nodesToReturn;
}

/**
 * Updates the specific field of the filter in a node by mutating the existing
 * value.
 *
 * Motivation: This gets called within an immer produce function call, which
 * will detect the change and produce a new FlatSearchTree object reflecting the
 * changes.
 */
/**
 * Gets the value at a given filterNameAndField path from a flat tree leaf node.
 * Mirrors the path logic used in updateFlatSearchTreeNodeFilterFieldValueInPlace.
 */
export function getValueAtPathFromFlatNode(
  node: FlatSearchTreeLeafNode,
  filterNameAndField: string
): unknown {
  const filter = node.filter;
  if (!('value' in filter)) return undefined;

  const keyPath = filterNameAndField.split('.');

  if (keyPath[0] !== filter.name) {
    return undefined;
  }

  if (keyPath.length === 1) {
    return filter.value;
  }

  let current: unknown = filter.value;
  for (let i = 1; i < keyPath.length; i++) {
    if (current && typeof current === 'object' && keyPath[i] in (current as object)) {
      current = (current as Record<string, unknown>)[keyPath[i]];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * Updates the specific field of the filter in a node by mutating the existing
 * value.
 *
 * Motivation: This gets called within an immer produce function call, which
 * will detect the change and produce a new FlatSearchTree object reflecting the
 * changes.
 */
export function updateFlatSearchTreeNodeFilterFieldValueInPlace(
  flatSearchTree: FlatSearchTree,
  nodeId: string,
  { filterNameAndField, value }: FilterNameAndFieldMappedValue
): void {
  const node = flatSearchTree.nodes[nodeId];
  if (!node) throw new Error('Node not found');
  if (!isFlatSearchTreeLeafNode(node)) throw new Error('Node is not a leaf node');

  const filter = node.filter;
  if (!('value' in filter))
    throw new Error(
      "Filter missing value. This means its TestOnlyContactIdsRangeFilter, NoneFilter or AllFilter which shouldn't happen"
    );

  // What about ContactIdsFilter? That shouldn't have placeholder

  const keyPath = filterNameAndField.split('.');

  // The very first one is just the name of the filter
  if (keyPath[0] !== filter.name) {
    throw new Error(
      `Filter name mismatch: node filter name = '${filter.name}' but payload filterNameAndField = '${filterNameAndField}'`
    );
  }

  if (keyPath.length === 1) {
    // This non-compound filter that has no nested fields
    // We make a type assertion here which isn't ideal, but we know the types
    // match based on previous checks
    (filter.value as TemplatePlaceholderFilterNameAndFieldToFilterValueTypeMap[typeof filterNameAndField]) =
      value;
  } else {
    // Otherwise we need to drill down.

    // We don't want to get the value of the very last field, we want to update
    // it in place so we stop at its parent
    const indexOfFieldParent = keyPath.length - 1;

    let current: unknown = filter.value;
    // We skip the first one, since that is the filter name
    for (let i = 1; i <= indexOfFieldParent; i++) {
      if (i === indexOfFieldParent) {
        // We're at the parent of the field, so we update in place
        const nestedFilter = current as Record<string, unknown>;
        if (filterNameAndField === 'callingStatus.callingStatusDateRange.callingPeriodQuantity') {
          // Special handling for this object
          nestedFilter['callingPeriodQuantity'] = value.callingPeriodQuantity;
          nestedFilter['callingPeriodDetails'] = value.callingPeriodDetails;
        } else {
          nestedFilter[keyPath[indexOfFieldParent]] = value;
        }
      } else if (current && typeof current === 'object' && keyPath[i] in current) {
        current = (current as Record<string, unknown>)[keyPath[i]];
      } else {
        throw new Error(`Invalid key path: ${keyPath.join('.')}`);
      }
    }
  }
}
